import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import deepEqual from 'fast-deep-equal'
import { useSetRecoilState } from 'recoil'
import { useUser } from '../../hooks/useUser'
import { normalizePromotionRuleOperators } from '../../util/promotions'
import { Layout } from '../Layout'
import PromotionRuleI = Models.PromotionRuleI
import PromotionRuleItemsI = Models.PromotionRuleItemsI
import Promotion = Models.Promotion

import { isValidNumericInput } from '../../util/numeric_input'
import { parseFloatAndCoerce } from '../../util/promotionBidCoerce'
import { activeRuleAtom } from './promotion_state'
import { deriveRecommendMaxLookbackDays } from './PromotionRuleEditorPanel'
import { PromotionRuleEditorNew } from './PromotionRuleEditorNew'
import { detectConflict } from './promotionRuleHelpers'

interface PromotionRuleProps {
    promotion: Promotion
    onSave: (rule: PromotionRuleI) => any
    initialRule: PromotionRuleI
}

export const PromotionRule = ({ promotion, onSave, initialRule }: PromotionRuleProps) => {
    const setActiveRule = useSetRecoilState(activeRuleAtom)
    const normalizedInitialRule = useMemo(
        () => normalizePromotionRuleOperators({ ...initialRule }),
        [initialRule],
    )
    const [rule, setRule] = useState<PromotionRuleI>(normalizedInitialRule)
    const validating = false
    const [conflict, setConflict] = useState<boolean>(false)
    const { user, is_kdp } = useUser()

    useEffect(() => {
        setRule(normalizedInitialRule)
    }, [normalizedInitialRule])

    const setRuleValue =
        <K extends keyof PromotionRuleI>(key: K) =>
        (value: string) => {
            const numericalFields = [
                'orders_value',
                'adjusted_orders_value',
                'spend_value',
                'acos_value',
                'blended_acos_value',
                'clicks_value',
                'adjusted_pages_read_value',
                'adjusted_estimated_royalties_value',
                'blended_profit_value',
                'lookback_days',
                'bid_increase_amount',
                'bid_increase_percent',
                // Without this gate, comma decimals (e.g. "0,35" from a Spanish
                // user) reached Promotion::formatBid as `100 * '0,35'` and
                // PHP-warning-500'd. ref Bugsnag 6a08b6b18c3285d1a592033d.
                'bid_set',
            ]

            const negativesAllowed: Partial<Record<keyof PromotionRuleI, boolean>> = {
                bid_increase_amount: true,
                bid_increase_percent: true,
            }

            const shouldBeNumeric = numericalFields.includes(key)
            const allowDecimals = isDecimalField(key)
            if (
                shouldBeNumeric &&
                !isValidNumericInput(value, allowDecimals, negativesAllowed[key])
            ) {
                return
            }

            // We can only have either the bid_increase_amount or bid_increase_amount
            // set at any one time, so we clear the other one when one is set.
            if (key == 'bid_increase_percent') {
                setRule((prev) =>
                    normalizePromotionRuleOperators({
                        ...prev,
                        bid_increase_percent: value,
                        bid_increase_amount: null,
                    }),
                )
            } else if (key == 'bid_increase_amount') {
                setRule((prev) =>
                    normalizePromotionRuleOperators({
                        ...prev,
                        bid_increase_percent: null,
                        bid_increase_amount: value,
                    }),
                )
            } else {
                setRule((prev) =>
                    normalizePromotionRuleOperators({
                        ...prev,
                        [key]: value,
                    }),
                )
            }
        }

    const setRuleValues = (updates: Partial<PromotionRuleI>) => {
        setRule((prev) =>
            normalizePromotionRuleOperators({
                ...prev,
                ...updates,
            }),
        )
    }

    const save = useCallback(() => {
        // Strip out any unused rule items for db persistence
        const ruleFinal: PromotionRuleI = normalizePromotionRuleOperators({
            ...rule,
            rule_items: rule.rule_items.filter((item) => {
                return (
                    item.use_search_terms ||
                    item.promotion_broad ||
                    item.promotion_phrase ||
                    item.promotion_exact ||
                    item.promotion_products ||
                    item.negation_phrase ||
                    item.negation_exact ||
                    item.negation_products
                )
            }),
        })
        // Coerce every field that the backend's Promotion::formatBid touches.
        // A non-numeric value here (e.g. "0,2" from a Spanish-locale user)
        // would otherwise crash POST /api/v1/promotions with a 500. Keep this
        // list in sync with formatBid's call sites in app/Promotion.php.
        parseFloatAndCoerce(ruleFinal, 'bid_increase_amount')
        parseFloatAndCoerce(ruleFinal, 'bid_increase_percent')
        parseFloatAndCoerce(ruleFinal, 'bid_set')
        parseFloatAndCoerce(ruleFinal, 'spend_value')

        onSave(ruleFinal)
        setActiveRule(null)
    }, [onSave, rule, setActiveRule])

    // Conflict checks — delegated to promotionRuleHelpers.detectConflict.
    // See PROMOTION_RULE_BUSINESS_LOGIC.md §3 for the full spec.
    useEffect(() => {
        setConflict(detectConflict(rule?.rule_items ?? []))
    }, [rule, setConflict])

    const dirty = useMemo(
        () => !deepEqual(rule, normalizedInitialRule),
        [rule, normalizedInitialRule],
    )

    const shortcutStateRef = useRef({ validating, conflict })
    useEffect(() => {
        shortcutStateRef.current = { validating, conflict }
    }, [validating, conflict])

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const isSaveShortcut =
                (e.metaKey || e.ctrlKey) && (e.key?.toLowerCase() === 's' || e.code === 'KeyS')

            if (!isSaveShortcut) {
                return
            }

            e.preventDefault()
            e.stopPropagation()
            e.stopImmediatePropagation()

            const state = shortcutStateRef.current
            console.info('[SmartCampaign] save shortcut captured', {
                page: 'rule-detail',
                key: e.key,
                code: e.code,
                metaKey: e.metaKey,
                ctrlKey: e.ctrlKey,
                ...state,
            })
            if (!state.validating && !state.conflict) {
                save()
            } else {
                console.info('[SmartCampaign] save shortcut ignored', {
                    page: 'rule-detail',
                    reason: state.validating ? 'validating' : 'conflict',
                })
            }
        }

        window.addEventListener('keydown', handler, { capture: true })
        return () => window.removeEventListener('keydown', handler, { capture: true })
    }, [save])

    const toggleItem = (item: PromotionRuleItemsI, key: keyof PromotionRuleItemsI) => () => {
        setRule((rule) => ({
            ...rule,
            rule_items: rule.rule_items.map((i) => {
                if (i.campaign_id === item.campaign_id && i.ad_group_id === item.ad_group_id) {
                    i = {
                        ...i,
                        [key]: !i[key],
                    }
                }
                return i
            }),
        }))
    }

    const setRuleItem = (
        adGroupId: number,
        campaignId: number,
        updates: Partial<Record<keyof PromotionRuleItemsI, boolean>>,
    ) => {
        setRule((r) => ({
            ...r,
            rule_items: r.rule_items.map((i) =>
                i.ad_group_id === adGroupId && i.campaign_id === campaignId
                    ? { ...i, ...updates }
                    : i,
            ),
        }))
    }

    const { startDate, recommendMaxLookbackDays } = deriveRecommendMaxLookbackDays(user?.start_date)
    const { breadcrumbs, title } = renderBreadcrumbsAndTitle(promotion, rule)

    return (
        <Layout
            breadcrumbs={breadcrumbs}
            primary={title}
            actions={null}
            rightActions={null}
            layoutClassName="px-3 md:px-6 pb-6"
        >
            <PromotionRuleEditorNew
                key={rule.id ?? rule.uuid}
                rule={rule}
                setRuleValue={setRuleValue}
                setRuleValues={setRuleValues}
                currencySymbol={window.marketplace.currency_symbol}
                isKdp={is_kdp}
                startDate={startDate}
                recommendMaxLookbackDays={recommendMaxLookbackDays}
                onSetRuleItem={setRuleItem}
                conflict={conflict}
                onSave={save}
                onCancel={() => setActiveRule(null)}
                isSaving={validating}
                isNew={!rule.id}
                dirty={dirty}
            />
        </Layout>
    )
}

const isDecimalField = (fieldName: string): boolean => {
    // bid_set is a currency value in dollars (e.g. 0.35) — without it on this
    // whitelist, isValidNumericInput is called with allowDecimals=false and
    // rejects every realistic bid with "Only whole numbers are allowed!".
    // (Note: util/numeric_input.ts also defines an isDecimalField, but it is
    // not exported / imported here — this local copy is what actually runs.)
    const decimalFields = ['bid_increase_amount', 'bid_increase_percent', 'spend_value', 'bid_set']

    return decimalFields.includes(fieldName)
}

const renderBreadcrumbsAndTitle = (promotion: Promotion, activeRule: PromotionRuleI) => {
    const breadcrumbs = [
        { text: 'Automations' },
        { href: '/smart-campaigns', text: 'Smart Campaigns' },
        { text: promotion.name },
        { text: 'Rules' },
    ]

    if (activeRule.id === 0) {
        return {
            breadcrumbs,
            title: 'New Smart Campaign Rule',
        }
    } else {
        return {
            breadcrumbs,
            title: activeRule.name,
        }
    }
}

// `ConditionInput`, `InputWrapper`, and `OperatorSelect` were moved to
// PromotionRuleEditorPanel.tsx as part of the view/container split.
